import '@mobiscroll/react/dist/css/mobiscroll.min.css'
import { setOptions } from '@mobiscroll/react'
import { useMemo, useRef, useState, useEffect } from 'react'
import UserRota from './screens/UserRota/UserRota'
import ResidentRota from './screens/ResidentRota/ResidentRota'
import { useRoster } from '../hooks/useRoster'
import styled from 'styled-components'
import './common/rotaStyles.css'
import { Heading, MoreInfoLink, PageHeader, Tabs } from '@lmc/ui-kit'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Home } from '@lmc/ts-common'
import { getNominative } from '@lmc/common/utils'
import {
  CalendarOptions,
  getCalendarOptionsWithEventStack,
  themeOptions,
} from './common/constants'
import useFeatureFlag from 'hooks/useFeatureFlags'
import useMixpanelEvent, { MixpanelEvents } from 'hooks/useMixpanelEvent'
import {
  RotaBuilderFiltersType,
  RotaBuilderQueryFiltersType,
} from 'screens/Rostering/types'
import ShiftRota from './screens/ShiftRota/ShiftRota'
import { DAILY, WEEKLY } from '../constants'
import useCountChanges from '../hooks/useCountChanges'
import { AddRotaPatternDropdown } from './common/components'
import { getOverlayTypeByErrorCode } from '../utils'
import { checkHasFilters } from 'utils'
import useFilterParamsGeneric from 'hooks/useFilterParamsGeneric'
import dayjs from 'dayjs'

setOptions(themeOptions)

const RotaBuilder = () => {
  const { t } = useTranslation()

  const [active, setActive] = useState('1')

  const sendEvent = useMixpanelEvent()

  const [showAllEvents, setShowAllEvents] = useState(false)
  const [timelineView, setTimelineView] = useState(CalendarOptions.weekly)

  const { filterParamsGeneric, setFilterParams } =
    useFilterParamsGeneric<RotaBuilderQueryFiltersType>({
      selectedDate: dayjs().format('YYYY-MM-DD'),
    })

  const currentFiltersRef = useRef<RotaBuilderFiltersType>({})

  const filterParams: RotaBuilderFiltersType & { selectedDate?: undefined } =
    useMemo(() => {
      const newFilters: RotaBuilderFiltersType & { selectedDate?: undefined } =
        {
          ...filterParamsGeneric,
          selectedDate: undefined,
        }

      delete newFilters.selectedDate

      currentFiltersRef.current = newFilters

      return newFilters
    }, [filterParamsGeneric])

  const selectedDate = useMemo(
    () => new Date(filterParamsGeneric.selectedDate),
    [filterParamsGeneric.selectedDate],
  )

  const setSelectedDate = (newDate: Date) => {
    const currentFilters = currentFiltersRef.current
    setFilterParams(() => ({
      ...currentFilters,
      selectedDate: dayjs(newDate).format('YYYY-MM-DD'),
    }))
  }

  const isByShiftTab = active === '3'

  const combinedFilterParams = {
    ...filterParams,
    ...(isByShiftTab && { siteShift: true }),
  }

  const hasFilters = checkHasFilters(combinedFilterParams)

  const onFilterChange = (value: RotaBuilderFiltersType) => {
    setFilterParams((prevFilters) => ({
      ...prevFilters,
      ...value,
    }))
  }

  const flags = useFeatureFlag({
    flagNames: ['rostering_dev_features', 'published_rota', 'full_events_view'],
  })?.data

  const flagsRosteringDevFeaturesEnabled =
    flags?.rostering_dev_features?.enabled

  const flagsPublishedRotaEnabled = flags?.published_rota?.enabled

  const flagsFullEventsViewEnabled = flags?.full_events_view?.enabled

  const isDailyView =
    timelineView === CalendarOptions.daily ||
    timelineView === CalendarOptions.dailyZoomed

  const { countChangesData, refetchCountChanges } = useCountChanges({
    selectedDate,
    intervalType: isDailyView ? DAILY : WEEKLY,
    filterParams: combinedFilterParams,
    includeWarnings: true,
  })

  const {
    users,
    residents,
    usersRevisions,
    residentsRevisions,
    isRotaBuilderLoading,
    invalid,
    shifts,
    metadata,
    errorCode,
  } = useRoster({
    selectedDate,
    intervalType:
      timelineView === CalendarOptions.daily ||
      timelineView === CalendarOptions.dailyZoomed
        ? DAILY
        : WEEKLY,
    filterParams: combinedFilterParams,
    totalEvents: hasFilters
      ? countChangesData.filteredTotal || 0
      : countChangesData.totalEvents || 0,
  })

  const preferredPeopleNominative = useSelector(
    (state: { global: { home: Home } }) =>
      state.global.home.preferredPeopleNominative,
  )
  const residentsLabel = `By ${getNominative(
    preferredPeopleNominative,
    'Resident',
  )}`

  useEffect(() => {
    sendEvent(MixpanelEvents.ROTA_BUILDER_TAB_VIEWED, {
      tabKey: '1',
      tabLabel: t('BY_TEAM_MEMBER'),
      isInitialLoad: true,
      viewType: isDailyView ? 'daily' : 'weekly',
      timelineView: timelineView,
      source: 'rota_builder',
    })
  }, [])

  const overlayType = getOverlayTypeByErrorCode(errorCode)

  const handleToggleShowAllEvents = () => {
    setShowAllEvents((prev) => !prev)

    sendEvent(MixpanelEvents.ROTA_EVENTS_VIEW_TOGGLED, {
      viewType: !showAllEvents ? 'expanded' : 'collapsed',
      tab:
        active === '1'
          ? 'By Team Member'
          : active === '2'
            ? 'By Resident'
            : 'By Shift',
    })
  }

  const calendarOptions = getCalendarOptionsWithEventStack(
    showAllEvents,
    flagsFullEventsViewEnabled,
  )

  const handleTabs = (tab: string) => {
    setActive(tab)

    const getTabLabel = (tabKey: string) => {
      switch (tabKey) {
        case '1':
          return t('BY_TEAM_MEMBER')
        case '2':
          return residentsLabel
        case '3':
          return t('BY_SHIFT')
        default:
          return 'Unknown'
      }
    }

    sendEvent(MixpanelEvents.ROTA_BUILDER_TAB_VIEWED, {
      tabKey: tab,
      tabLabel: getTabLabel(tab),
      isInitialLoad: false,
      viewType: isDailyView ? 'daily' : 'weekly',
      timelineView: timelineView,
      source: 'rota_builder',
    })
  }

  return (
    <Container>
      <PageHeader
        title={
          <BuilderTitle>
            <TitleHeading level={1}>
              {flagsPublishedRotaEnabled ? t('BUILDER') : t('ROTA_BUILDER')}
            </TitleHeading>
            {flagsRosteringDevFeaturesEnabled && (
              <MoreInfoLink helpArticleUrl="todo" />
            )}
          </BuilderTitle>
        }
      >
        <AddRotaPatternDropdown />
      </PageHeader>
      <Tabs
        onChange={handleTabs}
        activeKey={active}
        tabBarStyle={tabBarStyle}
        items={[
          {
            key: '1',
            label: t('BY_TEAM_MEMBER'),
            children: (
              <UserRota
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
                timelineView={timelineView}
                setTimelineView={setTimelineView}
                publishedRota={false}
                invalid={invalid}
                resources={users}
                events={usersRevisions}
                isLoading={isRotaBuilderLoading}
                filterParams={filterParams}
                onFilterChange={onFilterChange}
                isDailyView={isDailyView}
                refetchCountChanges={refetchCountChanges}
                countChangesData={countChangesData}
                metadata={metadata}
                overlayType={overlayType}
                calendarOptions={calendarOptions}
                showAllEvents={showAllEvents}
                onToggleShowAllEvents={
                  flagsFullEventsViewEnabled
                    ? handleToggleShowAllEvents
                    : undefined
                }
              />
            ),
          },
          {
            key: '2',
            label: residentsLabel,
            children: (
              <ResidentRota
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
                timelineView={timelineView}
                setTimelineView={setTimelineView}
                publishedRota={false}
                resources={residents}
                events={residentsRevisions}
                isLoading={isRotaBuilderLoading}
                filterParams={filterParams}
                onFilterChange={onFilterChange}
                isDailyView={isDailyView}
                refetchCountChanges={refetchCountChanges}
                countChangesData={countChangesData}
                metadata={metadata}
                overlayType={overlayType}
                calendarOptions={calendarOptions}
                showAllEvents={showAllEvents}
                onToggleShowAllEvents={
                  flagsFullEventsViewEnabled
                    ? handleToggleShowAllEvents
                    : undefined
                }
              />
            ),
          },
          {
            key: '3',
            label: t('BY_SHIFT'),
            children: (
              <ShiftRota
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
                timelineView={timelineView}
                setTimelineView={setTimelineView}
                publishedRota={false}
                resources={shifts}
                events={residentsRevisions?.map((item) => ({
                  ...item,
                  resource: item.instanceType || '',
                }))}
                isLoading={isRotaBuilderLoading}
                filterParams={filterParams}
                onFilterChange={onFilterChange}
                isDailyView={isDailyView}
                refetchCountChanges={refetchCountChanges}
                countChangesData={countChangesData}
                calendarOptions={calendarOptions}
                showAllEvents={showAllEvents}
                onToggleShowAllEvents={
                  flagsFullEventsViewEnabled
                    ? handleToggleShowAllEvents
                    : undefined
                }
              />
            ),
          },
        ]}
      />
    </Container>
  )
}

const tabBarStyle = { paddingLeft: 24 }

const Container = styled.div`
  --rota-container-vertical-padding: 24px;

  padding: var(--rota-container-vertical-padding, 24px) 48px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const BuilderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const TitleHeading = styled(Heading)`
  color: ${(props) => props.theme.color.neutral.color3};
`

export default RotaBuilder
